import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Snackbar from '@mui/material/Snackbar';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import DOMPurify from 'dompurify';

import empleados from '../../assets/empleados.svg';
import enps from '../../assets/enps.svg';
import encuesta from '../../assets/icons/encuesta.png';
import starIcon from '../../assets/icons/star_icon.png';
import last from '../../assets/last.svg';
import pulso from '../../assets/pulso.svg';
import MyCarousel from '../../components/MyCarousel/MyCarousel';
import { selectCompanyById } from '../../features/companies/companiesSlice';
import IconSidebar from '../../Layout/IconSidebar/IconSidebar';
import Navbar from '../../Layout/Navbar/Navbar';
import {
  fetchJourneyMapAPI,
  getJourneysCompanyAPI,
} from '../../services/getJourneyMap.service';
import client, { API } from '../../utils/axiosInstance';
import { formatearFechas } from '../../utils/fechaCierre';
import { isAdmin } from '../../utils/helpers';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from './Journey.module.css';

const theme = createTheme({
  palette: {
    blue: {
      main: '#03aae4',
    },
  },
});

const DEFAULT_SURVEY_IMAGES = [enps, pulso, empleados, last];

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '8px',
  p: 4,
};

export default function Journey() {
  const DEFAULT_ICON =
    'https://peopleintelligenceapi.azurewebsites.net/StaticFiles/Images/JourneyImages/onboarding.svg';
  const navigate = useNavigate();
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const currentCompany = useSelector((state) => state.companies.currentCompany);
  const company = useSelector((state) =>
    currentCompany ? selectCompanyById(state, currentCompany.id) : null
  );
  const [currentMapId, setCurrentMapId] = useState(null);
  const [slides, setSlides] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [journeyToDelete, setJourneyToDelete] = useState(null);
  const [selectedJourneys, setSelectedJourneys] = useState([]);
  const [multiDeleteMode, setMultiDeleteMode] = useState(false);
  const [multiVisibilityMode, setMultiVisibilityMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChangingVisibility, setIsChangingVisibility] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [visibilityAction, setVisibilityAction] = useState(null);
  const [visibilityFilter, setVisibilityFilter] = useState('all');

  // Estados para Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [alertType, setAlertType] = useState('success');

  const handleExplorar = () => {
    navigate('/journey/survey-template');
  };

  const handleSettings = () => {
    navigate('/journeysettings');
  };

  const getSurveyImage = (image) => {
    if (image) {
      return image;
    }
    const randomIndex = Math.floor(
      Math.random() * DEFAULT_SURVEY_IMAGES.length
    );
    return DEFAULT_SURVEY_IMAGES[randomIndex];
  };

  const fetchJourneys = async () => {
    if (!currentCompany || !currentMapId) {
      return;
    }
    const { data } = await getJourneysCompanyAPI(
      currentCompany.id,
      currentMapId
    );
    setSlides((slides) =>
      slides.map((slide) => ({
        ...slide,
        isCurrent: String(slide.id) === String(currentMapId), // Cambio aquí
      }))
    );
    setJourneys(data);
  };

  const handleSelectedMapId = ({ id }) => {
    const idString = String(id);
    setCurrentMapId(idString);
    // Guardar el mapa seleccionado en localStorage
    localStorage.setItem('selectedMapId', idString);
  };

  const fetchJourneyMaps = async () => {
    const MESSAGE = 'No tiene permiso para acceder a esta funcionalidad.';
    if (!userInfo) {
      return navigate('/dashboard', { state: { message: MESSAGE } });
    }
    const { role } = userInfo;
    const validRoles = ['Journey', 'Administrador'];
    if (!role.some((roleItem) => validRoles.includes(roleItem))) {
      alert('No tiene permiso para acceder a esta funcionalidad.');
      navigate('/dashboard');
      return;
    }
    if (!currentCompany) return;
    const { data } = await fetchJourneyMapAPI(currentCompany.id);

    // Intentar recuperar el mapa guardado en localStorage
    const savedMapId = localStorage.getItem('selectedMapId');

    // Verificar si el mapa guardado existe en los datos actuales
    const savedMapExists =
      savedMapId &&
      data.some((slide) => String(slide.id) === String(savedMapId));

    // Usar el mapa guardado si existe, sino usar el primero
    const initialMapId = savedMapExists
      ? savedMapId
      : data.length > 0
      ? String(data[0].id)
      : null;

    setSlides(
      data.map((slide) => ({
        ...slide,
        isCurrent: String(slide.id) === String(initialMapId),
      }))
    );

    if (!data.length) {
      return;
    }

    setCurrentMapId(initialMapId);
  };

  const patchVisibilitySurvey = async (surveyId, isEnabled) => {
    try {
      const response = await client.patch(
        `SurveyVisibility/${surveyId}/${isEnabled}`
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const handleIndividualVisibilityToggle = async (journey, e) => {
    e.stopPropagation();
    setIsChangingVisibility(true);
    try {
      const newVisibility = !journey.isVisible;
      await patchVisibilitySurvey(journey.id, newVisibility);
      setJourneys(
        journeys.map((j) =>
          j.id === journey.id ? { ...j, isVisible: newVisibility } : j
        )
      );
      const action = newVisibility ? 'habilitada' : 'deshabilitada';
      setSnackbarMessage(`Encuesta ${action} exitosamente`);
      setAlertType('success');
      setOpenSnackbar(true);
    } catch (error) {
      setSnackbarMessage(
        'Hubo un error al cambiar la visibilidad de la encuesta'
      );
      setAlertType('error');
      setOpenSnackbar(true);
    } finally {
      setIsChangingVisibility(false);
    }
  };

  const handleChangeMultipleVisibility = async (surveyIds, isEnabled) => {
    setIsChangingVisibility(true);
    try {
      const visibilityPromises = surveyIds.map((id) =>
        patchVisibilitySurvey(id, isEnabled)
      );
      const responses = await Promise.all(visibilityPromises);
      const allSuccess = responses.every((response) => response.status === 200);
      if (allSuccess) {
        const action = isEnabled ? 'habilitadas' : 'deshabilitadas';
        setSnackbarMessage(
          `${surveyIds.length} encuestas ${action} satisfactoriamente`
        );
        setAlertType('success');
        setJourneys(
          journeys.map((j) =>
            surveyIds.includes(j.id) ? { ...j, isVisible: isEnabled } : j
          )
        );
        setSelectedJourneys([]);
        setMultiVisibilityMode(false);
      } else {
        setSnackbarMessage(
          'Algunas encuestas no pudieron cambiar su visibilidad'
        );
        setAlertType('warning');
      }
      setOpenSnackbar(true);
      setOpenModal(false);
    } catch (error) {
      setSnackbarMessage(
        'Hubo un error al momento de cambiar la visibilidad de las encuestas'
      );
      setOpenSnackbar(true);
      setAlertType('error');
    } finally {
      setIsChangingVisibility(false);
    }
  };

  const handleDeleteSurvey = async (idSurvey) => {
    setIsDeleting(true);
    try {
      const response = await client.delete(`deleteSurvey/${idSurvey}`);
      if (response.status === 200) {
        setSnackbarMessage('Encuesta eliminada satisfactoriamente');
        setOpenSnackbar(true);
        setAlertType('success');
        setJourneys(journeys.filter((j) => j.id !== idSurvey));
        setOpenModal(false);
        setJourneyToDelete(null);
      }
    } catch (error) {
      setSnackbarMessage('Hubo un error al momento de eliminar la encuesta');
      setOpenSnackbar(true);
      setAlertType('error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteMultipleSurveys = async (surveyIds) => {
    setIsDeleting(true);
    try {
      const deletePromises = surveyIds.map((id) =>
        client.delete(`deleteSurvey/${id}`)
      );
      const responses = await Promise.all(deletePromises);
      const allSuccess = responses.every((response) => response.status === 200);
      if (allSuccess) {
        setSnackbarMessage(
          `${surveyIds.length} encuestas eliminadas satisfactoriamente`
        );
        setAlertType('success');
        setJourneys(journeys.filter((j) => !surveyIds.includes(j.id)));
        setSelectedJourneys([]);
        setMultiDeleteMode(false);
      } else {
        setSnackbarMessage('Algunas encuestas no pudieron ser eliminadas');
        setAlertType('warning');
      }
      setOpenSnackbar(true);
      setOpenModal(false);
    } catch (error) {
      setSnackbarMessage('Hubo un error al momento de eliminar las encuestas');
      setOpenSnackbar(true);
      setAlertType('error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = (journey, e) => {
    e.stopPropagation();
    setJourneyToDelete(journey);
    setModalAction('delete');
    setOpenModal(true);
  };

  const handleConfirmDelete = async () => {
    if (journeyToDelete) {
      await handleDeleteSurvey(journeyToDelete.id);
    }
  };

  const handleToggleMultiDelete = () => {
    setMultiDeleteMode(!multiDeleteMode);
    setMultiVisibilityMode(false);
    setSelectedJourneys([]);
  };

  const handleToggleMultiVisibility = () => {
    setMultiVisibilityMode(!multiVisibilityMode);
    setMultiDeleteMode(false);
    setSelectedJourneys([]);
  };

  const handleSelectJourney = (journeyId) => {
    setSelectedJourneys((prev) => {
      if (prev.includes(journeyId)) {
        return prev.filter((id) => id !== journeyId);
      }
      return [...prev, journeyId];
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allFilteredIds = filteredJourneys.map((j) => j.id);
      setSelectedJourneys(allFilteredIds);
    } else {
      setSelectedJourneys([]);
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedJourneys.length === 0) {
      setSnackbarMessage('Selecciona al menos una encuesta');
      setOpenSnackbar(true);
      setAlertType('warning');
      return;
    }
    setModalAction('delete');
    setOpenModal(true);
  };

  const handleVisibilityMultiple = async (action) => {
    if (selectedJourneys.length === 0) {
      setSnackbarMessage('Selecciona al menos una encuesta');
      setOpenSnackbar(true);
      setAlertType('warning');
      return;
    }
    setModalAction('visibility');
    setVisibilityAction(action);
    setOpenModal(true);
  };

  const handleConfirmMultipleDelete = async () => {
    await handleDeleteMultipleSurveys(selectedJourneys);
  };

  const handleConfirmMultipleVisibility = async () => {
    const isEnabled = visibilityAction === 'enable';
    await handleChangeMultipleVisibility(selectedJourneys, isEnabled);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setJourneyToDelete(null);
    setModalAction(null);
    setVisibilityAction(null);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  useEffect(() => {
    fetchJourneys(currentMapId);
  }, [currentMapId]);

  useEffect(() => {
    fetchJourneyMaps();
  }, [currentCompany]);

  const nonObligatoryJourneys = journeys.filter(
    (journey) => !journey.isObligatory
  );

  const filteredJourneys = nonObligatoryJourneys.filter((journey) => {
    if (visibilityFilter === 'all') return true;
    if (visibilityFilter === 'visible') return journey.isVisible;
    if (visibilityFilter === 'hidden') return !journey.isVisible;
    return true;
  });

  const allSelected =
    filteredJourneys.length > 0 &&
    selectedJourneys.length === filteredJourneys.length;
  const someSelected =
    selectedJourneys.length > 0 &&
    selectedJourneys.length < filteredJourneys.length;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex' }}>
        <Navbar />
        <IconSidebar />
        <div style={{ backgroundColor: 'white' }}>
          <div className={styles.content}>
            <div className={styles.journey}>
              <MyCarousel
                slides={slides.map((slide) => ({
                  id: slide.id,
                  title: slide.mapJourney,
                  icon: slide.iconUrl ?? DEFAULT_ICON,
                  isCurrent: slide.isCurrent,
                  number: Number(slide.numberMap),
                }))}
                onSelected={(slide) => handleSelectedMapId(slide)}
              />

              <div className={styles.heading}>
                <div style={{ paddingRight: '16px' }}>
                  <img
                    src={company?.Logotipo ?? starIcon}
                    alt=""
                    className={styles.icon}
                  />
                </div>
                <div style={{ paddingRight: '1em' }} className={styles.text}>
                  <h1
                    style={{
                      fontSize: '24px',
                      letterSpacing: 0,
                      fontWeight: '500',
                      margin: 0,
                    }}
                  >
                    Experiencia del empleado.
                  </h1>
                </div>

                {isAdmin(userInfo) && (
                  <>
                    {nonObligatoryJourneys.length > 0 && (
                      <>
                        <FormControlLabel
                          control={
                            <select
                              value={visibilityFilter}
                              onChange={(e) =>
                                setVisibilityFilter(e.target.value)
                              }
                              style={{
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: '1px solid #03aae4',
                                color: '#03aae4',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                              }}
                            >
                              <option value="all">Todas las encuestas</option>
                              <option value="visible">Solo habilitadas</option>
                              <option value="hidden">
                                Solo deshabilitadas
                              </option>
                            </select>
                          }
                          label=""
                          style={{ marginRight: '1.5em' }}
                        />

                        <Button
                          variant={multiDeleteMode ? 'contained' : 'outlined'}
                          className={styles.explorar}
                          style={{
                            color: multiDeleteMode ? 'white' : '#03aae4',
                            marginRight: '1.5em',
                          }}
                          color="blue"
                          onClick={handleToggleMultiDelete}
                        >
                          {multiDeleteMode ? 'Cancelar' : 'Eliminar Múltiples'}
                        </Button>

                        <Button
                          variant={
                            multiVisibilityMode ? 'contained' : 'outlined'
                          }
                          className={styles.explorar}
                          style={{
                            color: multiVisibilityMode ? 'white' : '#03aae4',
                            marginRight: '1.5em',
                          }}
                          color="blue"
                          onClick={handleToggleMultiVisibility}
                        >
                          {multiVisibilityMode
                            ? 'Cancelar'
                            : 'Cambiar Visibilidad'}
                        </Button>
                      </>
                    )}

                    {(multiDeleteMode || multiVisibilityMode) &&
                      filteredJourneys.length > 0 && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={handleSelectAll}
                              color="primary"
                            />
                          }
                          label={
                            <Typography
                              style={{ fontWeight: '500', fontSize: '14px' }}
                            >
                              {allSelected
                                ? `Todas (${filteredJourneys.length})`
                                : someSelected
                                ? `${selectedJourneys.length} de ${filteredJourneys.length}`
                                : 'Seleccionar todas'}
                            </Typography>
                          }
                          style={{ marginRight: '1.5em' }}
                        />
                      )}

                    {multiDeleteMode && selectedJourneys.length > 0 && (
                      <Button
                        variant="contained"
                        style={{
                          backgroundColor: '#d32f2f',
                          color: 'white',
                          marginRight: '1.5em',
                        }}
                        onClick={handleDeleteMultiple}
                      >
                        Eliminar ({selectedJourneys.length})
                      </Button>
                    )}

                    {multiVisibilityMode && selectedJourneys.length > 0 && (
                      <>
                        <Button
                          variant="contained"
                          style={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            marginRight: '0.5em',
                          }}
                          onClick={() => handleVisibilityMultiple('enable')}
                          startIcon={<VisibilityIcon />}
                        >
                          Habilitar ({selectedJourneys.length})
                        </Button>
                        <Button
                          variant="contained"
                          style={{
                            backgroundColor: '#ff9800',
                            color: 'white',
                            marginRight: '1.5em',
                          }}
                          onClick={() => handleVisibilityMultiple('disable')}
                          startIcon={<VisibilityOffIcon />}
                        >
                          Deshabilitar ({selectedJourneys.length})
                        </Button>
                      </>
                    )}

                    <Button
                      variant="contained"
                      className={styles.explorar}
                      style={{
                        color: 'white',
                        marginRight: '1.5em',
                      }}
                      color="blue"
                      onClick={handleSettings}
                    >
                      Administra Encuestas
                    </Button>
                  </>
                )}

                <Button
                  variant="contained"
                  className={styles.explorar}
                  style={{
                    color: 'white',
                  }}
                  color="blue"
                  onClick={handleExplorar}
                >
                  Crear Encuestas
                </Button>
              </div>

              <div className={styles.templates}>
                {journeys
                  .filter((journey) => journey.isObligatory)
                  .map((journey) => {
                    return (
                      <div key={journey.id} className={styles.card}>
                        <img
                          src={getSurveyImage(journey.imageSurvey)}
                          alt=""
                          width="146"
                          height="81"
                          style={{ maxWidth: '100%', height: 'auto' }}
                        />
                        <p
                          style={{
                            fontSize: '18px',
                            letterSpacing: '0.5px',
                            fontWeight: '500',
                          }}
                        >
                          {<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(journey.nameSurvey) }} />}
                        </p>
                        <p
                          style={{
                            fontSize: '14px',
                            letterSpacing: '0.25px',
                            fontWeight: '300',
                          }}
                        >
                          {<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(journey.descriptionSurvey) }} />}
                        </p>
                        <Button
                          variant="outlined"
                          style={{
                            color: '#03aae4',
                            width: '30%',
                            alignSelf: 'flex-end',
                          }}
                          color="blue"
                          onClick={() =>
                            navigate(`/journey/survey/${journey.id}/detail`)
                          }
                        >
                          Empezar
                        </Button>
                      </div>
                    );
                  })}
                {filteredJourneys.map((journey) => {
                  return (
                    <div
                      key={journey.id}
                      className={styles.encuestacard}
                      onClick={() =>
                        !(multiDeleteMode || multiVisibilityMode) &&
                        navigate(`/journey/survey/${journey.id}/detail`)
                      }
                      style={{
                        cursor:
                          multiDeleteMode || multiVisibilityMode
                            ? 'default'
                            : 'pointer',
                        position: 'relative',
                      }}
                    >
                      {(multiDeleteMode || multiVisibilityMode) && (
                        <Checkbox
                          checked={selectedJourneys.includes(journey.id)}
                          onChange={() => handleSelectJourney(journey.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            zIndex: 1,
                          }}
                        />
                      )}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: '#EAF8FF',
                              width: '35px',
                              height: '35px',
                              padding: '12px',
                              borderRadius: '8px',
                              margin: '0.5em',
                            }}
                          >
                            <img
                              src={encuesta}
                              alt=""
                              style={{
                                width: '100%',
                                height: 'auto',
                                maxWidth: '100%',
                                verticalAlign: 'middle',
                              }}
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '1em',
                              paddingLeft: '12px',
                              width: '80%',
                            }}
                          >
                            <p
                              style={{
                                fontSize: '18px',
                                letterSpacing: '0.5px',
                                fontWeight: '500',
                              }}
                            >
                              {<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(journey.nameSurvey) }} />}
                            </p>
                            <div
                              style={{
                                overflow: 'hidden',
                                wordBreak: 'break-word',
                              }}
                            >
                              <p
                                style={{
                                  fontSize: '14px',
                                  letterSpacing: '0.25px',
                                  fontWeight: '300',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word',
                                }}
                               >
                                {<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(journey.descriptionSurvey) }} />}
                              </p>
                            </div>
                            {formatearFechas(journey.startDate, journey.answerDate) && (
                              <p
                                style={{
                                  fontSize: '12px',
                                  letterSpacing: '0.25px',
                                  color: '#757575',
                                  margin: 0,
                                }}
                              >
                                {formatearFechas(journey.startDate, journey.answerDate)}
                              </p>
                            )}
                          </div>
                        </div>
                        {!(multiDeleteMode || multiVisibilityMode) &&
                          isAdmin(userInfo) && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <IconButton
                                onClick={(e) =>
                                  handleIndividualVisibilityToggle(journey, e)
                                }
                                style={{
                                  color: journey.isVisible
                                    ? '#4caf50'
                                    : '#757575',
                                }}
                                disabled={isChangingVisibility}
                              >
                                {journey.isVisible ? (
                                  <VisibilityIcon />
                                ) : (
                                  <VisibilityOffIcon />
                                )}
                              </IconButton>
                              <IconButton
                                onClick={(e) => handleDeleteClick(journey, e)}
                                style={{
                                  color: '#d32f2f',
                                }}
                                disabled={isDeleting}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })}
                {filteredJourneys.length === 0 &&
                  nonObligatoryJourneys.length > 0 && (
                    <Alert severity="info">
                      No se encontraron encuestas con el filtro seleccionado.
                    </Alert>
                  )}
                {journeys.length === 0 && (
                  <Alert severity="info">No se encontraron encuestas.</Alert>
                )}
              </div>
            </div>
          </div>
        </div>
      </Box>

      <Modal
        open={openModal}
        onClose={
          !(isDeleting || isChangingVisibility) ? handleCloseModal : undefined
        }
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            style={{ marginBottom: '16px' }}
          >
            {modalAction === 'delete'
              ? selectedJourneys.length > 0
                ? '¿Requieres eliminar estas encuestas?'
                : '¿Requieres eliminar esta encuesta?'
              : modalAction === 'visibility'
              ? visibilityAction === 'enable'
                ? '¿Requieres habilitar estas encuestas?'
                : '¿Requieres deshabilitar estas encuestas?'
              : ''}
          </Typography>
          <Typography id="modal-description" sx={{ mb: 3 }}>
            {modalAction === 'delete'
              ? selectedJourneys.length > 0
                ? `Se eliminarán ${selectedJourneys.length} encuestas. Esta acción no se puede deshacer.`
                : journeyToDelete
                ? `Se eliminará la encuesta "${journeyToDelete.nameSurvey}". Esta acción no se puede deshacer.`
                : ''
              : modalAction === 'visibility'
              ? visibilityAction === 'enable'
                ? `Se habilitarán ${selectedJourneys.length} encuestas.`
                : `Se deshabilitarán ${selectedJourneys.length} encuestas.`
              : ''}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              disabled={isDeleting || isChangingVisibility}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              style={{
                backgroundColor:
                  modalAction === 'delete'
                    ? '#d32f2f'
                    : visibilityAction === 'enable'
                    ? '#4caf50'
                    : '#ff9800',
                color: 'white',
              }}
              onClick={
                modalAction === 'delete'
                  ? selectedJourneys.length > 0
                    ? handleConfirmMultipleDelete
                    : handleConfirmDelete
                  : handleConfirmMultipleVisibility
              }
              disabled={isDeleting || isChangingVisibility}
              startIcon={
                (isDeleting || isChangingVisibility) && (
                  <CircularProgress size={20} color="inherit" />
                )
              }
            >
              {isDeleting
                ? 'Eliminando...'
                : isChangingVisibility
                ? 'Procesando...'
                : 'Confirmar'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertType}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
